/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\AnnouncementSettingsSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/


import { Settings } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';

interface AnnouncementSettings {
  allow_terminators: boolean;
  terminator_key: string;
  post_delay: number;
  repeat_times: number;
  transfer_timeout: number | null;
  transfer_behavior: 'immediate' | 'delayed' | 'conditional';
  mode: 'once' | 'loop' | 'background';
  background_music: string;
  beep_before_playback: boolean;
  record_call: boolean;
  failover_module: string;
  failover_data: string;
  event_webhook: string;
  time_condition: { days: string[]; start: string; end: string } | null;
  after_hours_destination: { module: string; data: string } | null;
  max_duration: number | null;
}

interface AnnouncementSettingsSectionProps {
  settings: AnnouncementSettings;
  errors: Record<string, string>;
  onSettingChange: (field: keyof AnnouncementSettings, value: unknown) => void;
  t: (key: string, fallback?: string) => string;
}

export function AnnouncementSettingsSection({
  settings, errors, onSettingChange, t,
}: AnnouncementSettingsSectionProps) {
  return (
    <FormBox
      title={t('announcements.sections.settings')}
      icon={<Settings className="h-5 w-5" />}
    >
      <div className="grid items-center gap-4 sm:grid-cols-2 lg:grid-cols-4">

        {/* Post Delay */}
        <Label htmlFor="postDelay">
          <SimpleTooltip content={t('announcements.settings.postDelay.tooltip')}>
            {t('announcements.settings.postDelay.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="postDelay"
            type="number"
            value={settings.post_delay}
            onChange={(e) => onSettingChange('post_delay', Math.max(Number(e.target.value) || 0, 0))}
            min={0}
            placeholder="0"
          />
          {errors.post_delay && <p className="mt-1 text-sm text-red-500">{errors.post_delay}</p>}
        </div>

        {/* Repeat Times */}
        <Label htmlFor="repeatTimes">
          <SimpleTooltip content={t('announcements.settings.repeatTimes.tooltip')}>
            {t('announcements.settings.repeatTimes.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="repeatTimes"
            type="number"
            value={settings.repeat_times}
            onChange={(e) => onSettingChange('repeat_times', Math.min(Math.max(Number(e.target.value) || 1, 1), 5))}
            min={1}
            max={5}
            placeholder="1"
          />
          {errors.repeat_times && <p className="mt-1 text-sm text-red-500">{errors.repeat_times}</p>}
        </div>

        {/* Transfer Behavior */}
        <Label htmlFor="transferBehavior">
          <SimpleTooltip content={t('announcements.settings.transferBehavior.tooltip')}>
            {t('announcements.settings.transferBehavior.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <select
            id="transferBehavior"
            value={settings.transfer_behavior}
            onChange={(e) => onSettingChange('transfer_behavior', e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="immediate">{t('announcements.settings.transferBehavior.immediate')}</option>
            <option value="delayed">{t('announcements.settings.transferBehavior.delayed')}</option>
            <option value="conditional">{t('announcements.settings.transferBehavior.conditional')}</option>
          </select>
          {errors.transferBehavior && <p className="mt-1 text-sm text-red-500">{errors.transferBehavior}</p>}
        </div>

        {/* Mode */}
        <Label htmlFor="mode">
          <SimpleTooltip content={t('announcements.settings.mode.tooltip')}>
            {t('announcements.settings.mode.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <select
            id="mode"
            value={settings.mode}
            onChange={(e) => onSettingChange('mode', e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="once">{t('announcements.settings.mode.once')}</option>
            <option value="loop">{t('announcements.settings.mode.loop')}</option>
            <option value="background">{t('announcements.settings.mode.background')}</option>
          </select>
          {errors.mode && <p className="mt-1 text-sm text-red-500">{errors.mode}</p>}
        </div>

        {/* Allow Terminators */}
        <Label htmlFor="allowTerminators">
          <SimpleTooltip content={t('announcements.settings.allowTerminators.tooltip')}>
            {t('announcements.settings.allowTerminators.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle
            value={settings.allow_terminators}
            onChange={(value) => onSettingChange('allow_terminators', value)}
          />
        </div>

        {/* Terminator Key � conditional */}
        {settings.allow_terminators && (
          <>
            <Label htmlFor="terminatorKey">
              <SimpleTooltip content={t('announcements.settings.terminatorKey.tooltip')}>
                {t('announcements.settings.terminatorKey.label')}
              </SimpleTooltip>
            </Label>
            <div>
              <Input
                id="terminatorKey"
                value={settings.terminator_key}
                onChange={(e) => onSettingChange('terminator_key', e.target.value.slice(0, 1))}
                placeholder="#"
                maxLength={1}
              />
            </div>
          </>
        )}

        {/* Beep Before Playback */}
        <Label htmlFor="beepBeforePlayback">
          <SimpleTooltip content={t('announcements.settings.beepBeforePlayback.tooltip')}>
            {t('announcements.settings.beepBeforePlayback.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle
            value={settings.beep_before_playback}
            onChange={(value) => onSettingChange('beep_before_playback', value)}
          />
        </div>

        {/* Record Call */}
        <Label htmlFor="recordCall">
          <SimpleTooltip content={t('announcements.settings.recordCall.tooltip')}>
            {t('announcements.settings.recordCall.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle
            value={settings.record_call}
            onChange={(value) => onSettingChange('record_call', value)}
          />
        </div>

      </div>
    </FormBox>
  );
}
